/**
 * StatusFactorsTab.tsx
 *
 * Tab content for configuring status factors on a sphere.
 * Uses a split-pane master/detail layout with local draft state.
 */

import React, { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'localization';
import { useAvailableTypes } from '../../../hooks/useAvailableTypes';
import { confirmDialogAtom } from '../../atoms/ConfirmDialog/ConfirmDialogAtom';
import { Message } from 'primereact/message';
import { ProgressSpinner } from 'primereact/progressspinner';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import { FactorSidebarMolecule } from '../../molecules/FactorSidebar';
import { FactorDetailPaneMolecule } from '../../molecules/FactorDetailPane';
import { useStatusFactorsState, LastActiveFactorError } from '../../../hooks/useStatusFactorsState';
import { useSchemaType } from '../../../hooks/useSchemaType';
import {
  buildFilterPropertyOptions,
  buildFilterPerspectiveOptions,
  buildSchemaPerspectiveOptions,
  type SchemaForFilters,
} from '../../../utils/filterPropertyBuilder';
import {
  getInboundEligibleSchemas,
  getEligibleSchemaObjects,
} from '@center-platform/spheres/identity';
import { collectPerspectiveFiltersFromTree } from '@center-platform/spheres/filters';
import AspectCatalogMolecule from '../../molecules/AspectCatalog/AspectCatalogMolecule';
import { useUnifiedFilterCatalog } from '../../../hooks/useUnifiedFilterCatalog';
import { allowedLanesForSurface, resolveCatalogScopeSource } from '../../../utils/catalogBuilders';
import type { FilterGroupLeaf } from 'types/filters';
import type { Schema } from 'types/schema';
import type { Sphere } from 'types/global';
import type { FactorMode } from 'types/spherePoints';
import type { ApiClients } from '../../../context/ApiClientsContext';
import type { FactorFormState } from '../../../types/statusFactors';
import './StatusFactorsTab.scss';

export interface StatusFactorsTabProps {
  sphere: Sphere;
  organizationId: string;
  apiClients: ApiClients | null;
  hasEditPermission: boolean;
  onFactorAdded?: () => void;
  onFactorRemoved?: () => void;
  onFactorUpdated?: () => void;
  /** Called when the unsaved changes state changes */
  onUnsavedChangesChange?: (hasUnsaved: boolean) => void;
}

const StatusFactorsTab: React.FC<StatusFactorsTabProps> = ({
  sphere,
  organizationId,
  apiClients,
  hasEditPermission,
  onFactorAdded,
  onFactorRemoved,
  onFactorUpdated,
  onUnsavedChangesChange,
}) => {
  const { t } = useTranslation();

  const { schemas } = useAvailableTypes(apiClients, organizationId);
  const { schema: schemaDefinition } = useSchemaType(sphere.properties?.type ?? null);

  // Check if schema has status factors enabled
  const statusFactorsConfig = schemaDefinition?.statusFactors;
  const schemaHasStatusFactors = statusFactorsConfig?.enabled === true;
  const schemaFactors = statusFactorsConfig?.factors || [];
  const schemaFactorCount = schemaFactors.length;

  // Deploy state
  const [isDeploying, setIsDeploying] = useState(false);
  const [deployError, setDeployError] = useState<string | null>(null);

  // Per-factor view-mode state (read-first summary vs configure editor)
  const [viewModeByFactorId, setViewModeByFactorId] = useState<Record<string, 'summary' | 'configure'>>({});

  // Use the status factors state hook for managing local state with dirty tracking
  const {
    factors,
    deactivatedFactors,
    selectedFactorId,
    selectedFactor,
    dirtyFactorCount,
    loading,
    error,
    isSaving,
    selectFactor,
    addNewFactor,
    updateFactor,
    applyFactor,
    deleteFactor,
    revertFactor,
    reactivateFactor,
    setMode,
    setShowOnCards,
    refreshFromServer,
  } = useStatusFactorsState({
    sphere,
    apiClients,
    hasEditPermission,
    onFactorAdded,
    onFactorRemoved,
    onFactorUpdated,
    onUnsavedChangesChange,
  });


  /*
   * EAINT-927: WHO CHECKS IN perspective picker (inside CheckInScheduleEditor)
   * needs the perspectives THIS sphere's schema defines — not the union
   * across every schema in the org. Was calling `buildFilterPerspectiveOptions`
   * (correct for the related-spheres factor's cross-sphere query builder,
   * wrong here). Swap to `buildSchemaPerspectiveOptions` which was written
   * expressly for CheckInScheduleEditor per its jsdoc.
   *
   * The child (FactorDetailPane) declares its prop as `PerspectiveOption[]`
   * with a `label` field and does its own `.label → .displayName` mapping
   * before handing down to ManualFactorEditor. Adapt the emitted
   * `SimplePerspectiveOption` (`.displayName`) to that shape here so the
   * pane's type contract stays intact.
   */
  const availablePerspectives = useMemo(
    () =>
      buildSchemaPerspectiveOptions(
        schemaDefinition as SchemaForFilters | null,
        t,
        /* includeDefaultPerspectives */ true,
      ).map((p) => ({
        systemName: p.systemName,
        label: p.displayName,
      })),
    [schemaDefinition, t]
  );

  // Get eligible schemas based on direction and selected perspectives
  // This determines which schemas can be related to the current sphere
  const relatedSchemas = useMemo(() => {
    // Only apply contextual filtering for related_spheres factor type
    if (!schemaDefinition || selectedFactor?.localData.factorType !== 'related_spheres') {
      return schemas as Schema[]; // Fallback to all schemas
    }

    // Direction and perspective conditions are authored through the Rollup Set Card now
    // (StartFromBand / the NARROWED TO tree), not the legacy `perspectiveDirection`/
    // `perspectiveFilters` fields — those are set once on read and never updated as the
    // card is edited (EAINT-877 Exit Gate). `any` scope has no traversal direction to
    // narrow by, so it falls back to every schema, same as a non-related_spheres factor.
    const rollupSet = selectedFactor.localData.rollupSet;
    if (!rollupSet || rollupSet.scope === 'any' || !rollupSet.direction) {
      return schemas as Schema[];
    }
    const direction = rollupSet.direction;
    const selectedPerspectives = collectPerspectiveFiltersFromTree(rollupSet.tree)
      .filter((pf) => pf.operator === 'in')
      .map((pf) => pf.perspectiveSystemName);

    if (direction === 'inbound') {
      // What types can be assigned TO this sphere?
      return getInboundEligibleSchemas({
        currentSchema: schemaDefinition as Schema,
        allSchemas: schemas as Schema[],
        perspectiveSystemNames: selectedPerspectives.length > 0 ? selectedPerspectives : undefined,
      });
    } else {
      // What types can this sphere be assigned TO?
      const results = getEligibleSchemaObjects(schemas as Schema[], {
        assigneeSystemName: schemaDefinition.system_name,
        // Pass the parent schema's node_label so eligibility resolves even when the
        // parent's own type isn't among `schemas` (the available/provided types).
        assigneeNodeLabel: schemaDefinition.node_label,
        perspectiveSystemNames: selectedPerspectives.length > 0 ? selectedPerspectives : undefined,
      });
      return results.map((r) => r.schema);
    }
  }, [schemaDefinition, schemas, selectedFactor?.localData.factorType, selectedFactor?.localData.rollupSet]);

  // Further filter by type filters already applied (like TreeMap does) — walks nested
  // `filterGroups` too (`resolveCatalogScopeSource`/`collectFilterScopeLeaves`), not just the
  // top-level `propertyFilters` the old `.find()` read.
  const factorFilterScope = useMemo(
    () =>
      resolveCatalogScopeSource(
        {
          propertyFilters: selectedFactor?.localData.propertyFilters,
          filterGroups: selectedFactor?.localData.filterGroups,
        },
        schemas as SchemaForFilters[]
      ),
    [selectedFactor?.localData.propertyFilters, selectedFactor?.localData.filterGroups, schemas]
  );

  const eligibleSchemas = useMemo(() => {
    if (factorFilterScope.source !== 'filter') return relatedSchemas;
    return relatedSchemas.filter((s) => factorFilterScope.typeSystemNames.includes(s.system_name));
  }, [relatedSchemas, factorFilterScope]);

  /**
   * Names the types the related-spheres catalog is narrowed to — surfaced to
   * `AspectCatalogMolecule`'s scope banner and the tree's embedded catalog so
   * a factor filtered to one type never shows "No type in scope".
   */
  const scopeTypeLabels = useMemo(() => {
    if (factorFilterScope.source !== 'filter') return [];
    return factorFilterScope.typeSystemNames.map(
      (systemName) => (schemas as SchemaForFilters[]).find((s) => s.system_name === systemName)?.name ?? systemName
    );
  }, [factorFilterScope, schemas]);

  // Property options for sphere filters - built dynamically from eligible schemas
  const sphereFilterPropertyOptions = useMemo(
    () => buildFilterPropertyOptions({
      schemas: schemas as SchemaForFilters[],         // All schemas (for Type/Label dropdowns)
      eligibleSchemas: eligibleSchemas as SchemaForFilters[],  // Filtered (for custom properties/arcs)
      t,
      includeArcProperties: true,
      includeCustomProperties: true,
    }),
    [schemas, eligibleSchemas, t]
  );

  // The related-spheres filter tree's "Add condition" catalog: the same six-lane
  // catalog every other surface mounts, fetched against this tab's own clients and
  // the factor's eligible schemas. `allowedLanesForSurface('factor-related-spheres')`
  // now returns `catalogLanes: undefined` (EAINT-877 Phase 2 — every lane is
  // evaluated, none withheld), so no narrowing filter applies; a defined array on
  // another surface would still narrow the same way.
  const isRelatedSpheresFactor = selectedFactor?.localData.factorType === 'related_spheres';
  const relatedSpheresCatalog = useUnifiedFilterCatalog({
    apiClients,
    schemas: eligibleSchemas as SchemaForFilters[],
    t,
    enabled: isRelatedSpheresFactor,
  });
  const relatedSpheresCatalogLanes = allowedLanesForSurface('factor-related-spheres').catalogLanes;
  const relatedSpheresCatalogEntries = useMemo(
    () =>
      relatedSpheresCatalogLanes
        ? relatedSpheresCatalog.entries.filter((e) => relatedSpheresCatalogLanes.includes(e.lane))
        : relatedSpheresCatalog.entries,
    [relatedSpheresCatalog.entries, relatedSpheresCatalogLanes]
  );
  const relatedSpheresRecentEntries = useMemo(
    () =>
      relatedSpheresCatalogLanes
        ? relatedSpheresCatalog.recent.filter((e) => relatedSpheresCatalogLanes.includes(e.lane))
        : relatedSpheresCatalog.recent,
    [relatedSpheresCatalog.recent, relatedSpheresCatalogLanes]
  );
  const renderRelatedSpheresCatalog = useCallback(
    ({ onPick, onClose }: { onPick: (leaf: FilterGroupLeaf) => void; onClose: () => void }) => (
      <AspectCatalogMolecule
        entries={relatedSpheresCatalogEntries}
        recent={relatedSpheresRecentEntries}
        loading={relatedSpheresCatalog.loading}
        error={relatedSpheresCatalog.measuresError}
        onRetry={relatedSpheresCatalog.retryMeasures}
        onPick={(entry) => onPick(relatedSpheresCatalog.pickEntry(entry))}
        onCancel={onClose}
        scopeTypeLabels={scopeTypeLabels}
      />
    ),
    [relatedSpheresCatalog, relatedSpheresCatalogEntries, relatedSpheresRecentEntries, scopeTypeLabels]
  );

  // Handle factor selection — resets viewMode to 'summary' when switching factors
  const handleSelectFactor = useCallback((id: string | null) => {
    // If there's unsaved changes, warn the user
    if (selectedFactor?.isDirty) {
      confirmDialogAtom({
        message: t('statusFactors.confirmSwitchFactor', 'You have unsaved changes. Do you want to discard them?'),
        header: t('statusFactors.confirmSwitchFactorTitle', 'Unsaved Changes'),
        icon: 'pi pi-exclamation-triangle',
        variant: 'warning',
        accept: () => {
          selectFactor(id);
          if (id) setViewModeByFactorId((prev) => ({ ...prev, [id]: 'summary' }));
        },
        reject: () => { /* stay on current factor */ },
      });
    } else {
      selectFactor(id);
      if (id) setViewModeByFactorId((prev) => ({ ...prev, [id]: 'summary' }));
    }
  }, [selectedFactor?.isDirty, selectFactor, t]);

  // Handle adding a new factor
  const handleAddFactor = useCallback(() => {
    const newId = addNewFactor();
    selectFactor(newId);
  }, [addNewFactor, selectFactor]);

  // Handle factor field changes
  const handleFactorChange = useCallback((updates: Partial<FactorFormState>) => {
    if (selectedFactorId) {
      updateFactor(selectedFactorId, updates);
    }
  }, [selectedFactorId, updateFactor]);

  // Handle applying changes to a single factor
  const handleApply = useCallback(async () => {
    if (selectedFactorId) {
      await applyFactor(selectedFactorId);
    }
  }, [selectedFactorId, applyFactor]);

  // Handle deleting/deactivating a factor
  const handleDelete = useCallback(() => {
    if (!selectedFactorId || !selectedFactor) return;

    // Draft factors: simple discard confirmation
    if (selectedFactor.isNew) {
      confirmDialogAtom({
        message: t('statusFactors.confirmDiscardNew'),
        header: t('statusFactors.confirmDiscardNewTitle'),
        icon: 'pi pi-exclamation-triangle',
        variant: 'danger',
        accept: async () => {
          await deleteFactor(selectedFactorId);
        },
      });
      return;
    }

    // Existing factors: deactivation confirmation with consequences
    const factorName = selectedFactor.localData.name || selectedFactor.originalData?.name || '';
    confirmDialogAtom({
      header: t('statusFactors.confirmDeactivate.title', { name: factorName }),
      icon: 'pi pi-exclamation-triangle',
      variant: 'danger',
      acceptLabel: t('statusFactors.actions.deactivate'),
      rejectLabel: t('common.cancel'),
      message: t('statusFactors.confirmDeactivate.reactivateNote'),
      accept: async () => {
        try {
          await deleteFactor(selectedFactorId);
        } catch (err) {
          if (err instanceof LastActiveFactorError) {
            // Show second confirmation for last active factor
            confirmDialogAtom({
              header: t('statusFactors.confirmLastFactor.title'),
              message: t('statusFactors.confirmLastFactor.message', { name: factorName }),
              icon: 'pi pi-exclamation-triangle',
              variant: 'danger',
              acceptLabel: t('statusFactors.actions.deactivateAnyway'),
              rejectLabel: t('common.cancel'),
              accept: async () => {
                await deleteFactor(selectedFactorId, { confirmLastFactor: true });
              },
            });
          }
        }
      },
    });
  }, [selectedFactorId, selectedFactor, deleteFactor, t]);

  // Handle reverting changes - revert directly without confirmation
  const handleRevert = useCallback(() => {
    if (selectedFactorId) {
      revertFactor(selectedFactorId);
    }
  }, [selectedFactorId, revertFactor]);

  // Handle reactivating a deactivated factor
  const handleReactivate = useCallback(async () => {
    if (selectedFactorId) {
      await reactivateFactor(selectedFactorId);
    }
  }, [selectedFactorId, reactivateFactor]);

  // Handle setting the lifecycle mode (active / observing / paused)
  const handleSetMode = useCallback(async (mode: FactorMode) => {
    if (selectedFactorId) {
      await setMode(selectedFactorId, mode);
    }
  }, [selectedFactorId, setMode]);

  // EAINT-750: toggle the shared header show-on-cards switch (immediate PATCH).
  const handleSetShowOnCards = useCallback(async (showOnCards: boolean) => {
    if (selectedFactorId) {
      await setShowOnCards(selectedFactorId, showOnCards);
    }
  }, [selectedFactorId, setShowOnCards]);

  // Handle deploying schema factors
  const handleDeployFactors = useCallback(async () => {
    if (!apiClients?.graphClient || !sphere.id) return;

    setIsDeploying(true);
    setDeployError(null);

    try {
      await apiClients.graphClient.post(`/sphere/${sphere.id}/status/deploy-factors`, {
        recalculate: true,
      });
      // Refresh the factors list after deploy
      await refreshFromServer();
      onFactorAdded?.();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to deploy status factors';
      console.error('[StatusFactorsTab] Failed to deploy factors:', err);
      setDeployError(errorMessage);
    } finally {
      setIsDeploying(false);
    }
  }, [apiClients, sphere.id, refreshFromServer, onFactorAdded]);

  // Helper to get factor type label
  const getFactorTypeLabel = (factorType: string): string => {
    switch (factorType) {
      case 'manual':
        return t('statusFactors.types.manual.label', 'Manual Check-In');
      case 'related_spheres':
        return t('statusFactors.types.relatedSpheres.label', 'Related Spheres');
      case 'arc_health':
        return t('statusFactors.types.arcHealth.label');
      default:
        return factorType;
    }
  };

  // Render loading state
  if (loading) {
    return (
      <div className="status-factors-tab status-factors-tab--loading">
        <ProgressSpinner />
        <p>{t('statusFactors.loading', 'Loading status factors...')}</p>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="status-factors-tab status-factors-tab--error">
        <Message severity="error" text={error} />
      </div>
    );
  }

  // Check if we should show deploy prompt in detail pane
  const showDeployPrompt = factors.length === 0 && schemaHasStatusFactors && schemaFactorCount > 0 && !selectedFactor;

  return (
    <div className="status-factors-tab status-factors-tab--split-pane">
      {/* Header */}
      <div className="status-factors-tab__header">
        <div className="status-factors-tab__title-section">
          <h3>{t('statusFactors.title', 'Status Factors')}</h3>
          <p className="status-factors-tab__description">
            {t(
              'statusFactors.description',
              'Configure how the status of this sphere is calculated from various factors.'
            )}
          </p>
        </div>
      </div>

      {/* Split Pane Content */}
      <div className="status-factors-tab__split-pane">
        {/* Left Sidebar */}
        <FactorSidebarMolecule
          factors={factors}
          deactivatedFactors={deactivatedFactors}
          selectedFactorId={selectedFactorId}
          onSelectFactor={handleSelectFactor}
          onAddFactor={handleAddFactor}
          hasEditPermission={hasEditPermission}
          dirtyFactorCount={dirtyFactorCount}
          className="status-factors-tab__sidebar"
        />

        {/* Right Detail Pane */}
        {showDeployPrompt ? (
          <div className="status-factors-tab__detail status-factors-tab__deploy-pane">
            <div className="status-factors-tab__deploy-content">
              <div className="status-factors-tab__deploy-icon">🚀</div>
              <h4 className="status-factors-tab__deploy-title">
                {t('statusFactors.deploy.title', 'Status Factors Available')}
              </h4>
              <p className="status-factors-tab__deploy-description">
                {t(
                  'statusFactors.deploy.description',
                  'This sphere type has {{count}} status factors defined. Deploy them to start tracking status.',
                  { count: schemaFactorCount }
                )}
              </p>

              {/* Preview of schema factors */}
              <ul className="status-factors-tab__deploy-factors">
                {schemaFactors.map((factor: { factorType: string; weight?: number }, index: number) => (
                  <li key={`${factor.factorType}-${index}`} className="status-factors-tab__deploy-factor-item">
                    <span className="status-factors-tab__deploy-factor-name">
                      {getFactorTypeLabel(factor.factorType)}
                    </span>
                    <span className="status-factors-tab__deploy-factor-weight">
                      {t('statusFactors.weight', 'Weight')}: {factor.weight ?? 1}
                    </span>
                  </li>
                ))}
              </ul>

              {/* Deploy error */}
              {deployError && (
                <Message severity="error" text={deployError} className="status-factors-tab__deploy-error" />
              )}

              {/* Deploy button */}
              <ButtonAtom
                type="button"
                label={
                  isDeploying
                    ? t('statusFactors.deploy.deploying', 'Deploying...')
                    : t('statusFactors.deploy.button', 'Deploy Status Factors')
                }
                icon={isDeploying ? 'pi pi-spin pi-spinner' : 'pi pi-bolt'}
                onClick={(e: React.MouseEvent) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleDeployFactors();
                }}
                disabled={isDeploying || !apiClients}
                variant="primary"
                shape="pill"
              />

              <p className="status-factors-tab__deploy-note">
                <small>
                  {t(
                    'statusFactors.deploy.note',
                    'You can customize these factors after deployment, or add a custom factor using the + button.'
                  )}
                </small>
              </p>
            </div>
          </div>
        ) : (
          <FactorDetailPaneMolecule
            selectedFactor={selectedFactor}
            onFactorChange={handleFactorChange}
            onApply={handleApply}
            onDelete={handleDelete}
            onRevert={handleRevert}
            onReactivate={handleReactivate}
            onSetMode={handleSetMode}
            onSetShowOnCards={handleSetShowOnCards}
            availableProperties={sphereFilterPropertyOptions}
            availablePerspectives={availablePerspectives}
            perspectiveSearchProps={{
              schemaSystemName: sphere.properties?.type || '',
              sphereId: sphere.id,
            }}
            renderCatalog={renderRelatedSpheresCatalog}
            scopeTypeLabels={scopeTypeLabels}
            hasEditPermission={hasEditPermission}
            isSaving={isSaving}
            viewMode={selectedFactorId ? (viewModeByFactorId[selectedFactorId] ?? 'summary') : 'summary'}
            onSetViewMode={(mode) => {
              if (selectedFactorId) {
                setViewModeByFactorId((prev) => ({ ...prev, [selectedFactorId]: mode }));
              }
            }}
            sphereId={sphere.id}
            hostsMeasure={!!sphere.properties?.measureRole}
            className="status-factors-tab__detail"
          />
        )}
      </div>
    </div>
  );
};

StatusFactorsTab.displayName = 'StatusFactorsTab';

export default StatusFactorsTab;
